import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

export function CycleFields({ title, fields }) {
  return (
    <Box
      sx={{ p: { xs: 2, sm: 2.5 }, border: "1px solid #dce5ed", borderRadius: 2, bgcolor: "#fff" }}
    >
      <Typography sx={{ fontSize: ".76rem", fontWeight: 700, color: "#526579", mb: 2 }}>
        {title}
      </Typography>
      <Box
        component="dl"
        sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5 }}
      >
        {fields.map(([label, value]) => (
          <Box key={label} sx={{ minWidth: 0 }}>
            <Typography component="dt" sx={{ fontSize: ".74rem", color: "#526579", mb: 0.5 }}>
              {label}
            </Typography>
            <Typography
              component="dd"
              sx={{
                m: 0,
                fontSize: ".9rem",
                fontWeight: 600,
                color: "#183b50",
                overflowWrap: "anywhere",
              }}
            >
              {value ?? "Sin información"}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
CycleFields.propTypes = { title: PropTypes.string.isRequired, fields: PropTypes.array.isRequired };

export default function CycleDialog({
  open,
  onClose,
  busy,
  title,
  subtitle,
  children,
  actions,
  compact,
}) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth={compact ? "sm" : "md"}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: compact ? 560 : 780,
          maxHeight: "88dvh",
          m: { xs: 1.5, sm: 3 },
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ bgcolor: "#183b50", px: { xs: 2, sm: 3 }, py: 2.5, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ p: 1, bgcolor: "rgba(255,255,255,.12)", borderRadius: 2, display: "flex" }}>
            <DescriptionOutlinedIcon sx={{ color: "#d9edf5", fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="span"
              sx={{ display: "block", color: "#fff", fontWeight: 700, fontSize: "1.05rem" }}
            >
              {title}
            </Typography>
            <Typography
              component="span"
              sx={{ display: "block", color: "#d3e3ec", fontSize: ".76rem", mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          </Box>
          <IconButton
            aria-label="Cerrar modal"
            disabled={busy}
            onClick={onClose}
            sx={{
              color: "#fff",
              bgcolor: "rgba(255,255,255,.08)",
              "&:hover": { bgcolor: "rgba(255,255,255,.18)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          bgcolor: "#f4f7fa",
          px: { xs: 2, sm: 3 },
          py: 3,
          pt: "24px !important",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          bgcolor: "#fff",
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: "1px solid #dce5ed",
          flexShrink: 0,
          gap: 1,
          "& .MuiButton-outlined": { color: "#185a75", borderColor: "#90adbd" },
        }}
      >
        {actions}
      </DialogActions>
    </Dialog>
  );
}
CycleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  children: PropTypes.node,
  actions: PropTypes.node,
  compact: PropTypes.bool,
};
CycleDialog.defaultProps = { busy: false, compact: false, children: null, actions: null };
